import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { supabase, isSupabaseReady } from '../supabase';

const AuthContext = createContext();

// ── Check hash ONCE at module load time (before any React renders) ──────────
const hashOnLoad = window.location.hash || '';
const isRecoveryHash =
  hashOnLoad.includes('type=recovery') && hashOnLoad.includes('access_token');

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pre-seed recoveryMode from hash so first render already shows ResetPasswordPage
  const [recoveryMode, setRecoveryMode] = useState(isRecoveryHash);

  // Use a ref so the onAuthStateChange closure always reads the latest value
  const recoveryModeRef = useRef(isRecoveryHash);
  const setRecovery = (val) => {
    recoveryModeRef.current = val;
    setRecoveryMode(val);
  };

  const fetchProfile = async (userId) => {
    if (!isSupabaseReady) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) {
      console.error('[AuthContext] fetchProfile error —', error.message);
      return null;
    }
    return data;
  };

  const forceSignOut = async (reason) => {
    console.warn('[AuthContext] forceSignOut:', reason);
    try { await supabase.auth.signOut(); } catch (err) { console.error(err); }
    setSession(null);
    setProfile(null);
  };

  useEffect(() => {
    if (!isSupabaseReady) {
      try {
        const saved = localStorage.getItem('crm_session');
        if (saved) setSession(JSON.parse(saved));
      } catch (err) {
        console.warn('[AuthContext] Could not parse local session', err);
      }
      setLoading(false);
      return;
    }

    const restoreSession = async () => {
      // If we already detected a recovery hash, let Supabase exchange the token
      // via onAuthStateChange — don't try to restore a normal session.
      if (isRecoveryHash) {
        console.log('[AuthContext] Recovery hash detected on load — skipping normal session restore');
        setLoading(false);
        return;
      }

      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!s?.user) {
          setSession(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        const p = await fetchProfile(s.user.id);
        if (!p) {
          await forceSignOut('profile missing on restore');
          setLoading(false);
          return;
        }
        if (p.status !== 'active') {
          await forceSignOut('profile inactive on restore');
          setLoading(false);
          return;
        }
        setSession(s);
        setProfile(p);
      } catch (err) {
        console.error('[AuthContext] restoreSession error', err);
        await forceSignOut('unexpected error on restore');
      } finally {
        setLoading(false);
      }
    };

    restoreSession();

    // ── Auth state change listener ──────────────────────────────────────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        console.log('[AuthContext] onAuthStateChange: event=', event);

        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthContext] PASSWORD_RECOVERY event');
          setSession(s);
          setRecovery(true);
          setLoading(false);
          return;
        }

        if (event === 'SIGNED_OUT') {
          setSession(null);
          setProfile(null);
          setRecovery(false);
          setLoading(false);
          return;
        }

        // SIGNED_IN while in recovery — stay in recovery, don't validate profile yet
        if (event === 'SIGNED_IN' && recoveryModeRef.current) {
          console.log('[AuthContext] SIGNED_IN during recoveryMode — staying in recovery');
          setSession(s);
          setLoading(false);
          return;
        }

        if (event === 'TOKEN_REFRESHED') {
          setSession(s);
          return;
        }

        if (event === 'SIGNED_IN') {
          setSession(s);
          return;
        }

        if (s?.user) {
          const p = await fetchProfile(s.user.id);
          if (!p || p.status !== 'active') {
            await forceSignOut('invalid profile in fallback handler');
            return;
          }
          setSession(s);
          setProfile(p);
        } else {
          setSession(null);
          setProfile(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    if (!isSupabaseReady) return { error: null, fallback: true };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    const p = await fetchProfile(data.user.id);
    if (!p) {
      await forceSignOut('profile missing after login');
      return { error: 'User profile not found. Contact your administrator.' };
    }
    if (p.status !== 'active') {
      await forceSignOut('profile inactive after login');
      return { error: 'Your account is not active. Contact your administrator.' };
    }
    setProfile(p);
    setSession(data.session);
    return { error: null, profile: p };
  };

  const logout = async () => {
    if (!isSupabaseReady) {
      localStorage.removeItem('crm_session');
      setSession(null);
      setProfile(null);
      return;
    }
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setRecovery(false);
  };

  const exitRecoveryMode = () => {
    console.log('[AuthContext] exitRecoveryMode');
    setRecovery(false);
    setSession(null);
    setProfile(null);
    // Clear the hash so a page refresh doesn't re-trigger recovery mode
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  return (
    <AuthContext.Provider value={{
      session,
      profile,
      loading,
      recoveryMode,
      login,
      logout,
      exitRecoveryMode,
      isLoggedIn: !!session,
      isSupabaseReady,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);